import { skipToken } from "@reduxjs/toolkit/query";
import { automationArtifactIdSchema } from "@repo/app-types/entities/automationArtifact";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import type { FC } from "react";
import { Navigate, useParams } from "react-router";
import { useAutomationArtifactLocationQuery } from "../../api/entities/automationArtifact.js";
import { appPaths } from "../../app/appPaths.js";

/**
 * Playbook-free entry point for a saved report (`/agent-results/:resultId`),
 * used by notification emails so the emailed link bakes in neither a playbook
 * nor an automation. Resolves the result's home automation + playbook from the
 * result id alone and redirects to the canonical, playbook-scoped report page.
 * Falls back to home when the id is malformed or the report no longer resolves.
 */
export const AgentResultRedirectPage: FC = () => {
  const { resultId } = useParams();
  const parsedResultId = resultId
    ? automationArtifactIdSchema.safeParse(resultId)
    : undefined;

  const { data: location, isLoading } = useAutomationArtifactLocationQuery(
    parsedResultId?.success ? parsedResultId.data : skipToken,
  );

  if (!parsedResultId?.success) {
    return <Navigate to={appPaths.home.build()} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loader style={LoaderStyle.ZOOMIES} />
      </div>
    );
  }

  if (!location || !location.playbook_id) {
    return <Navigate to={appPaths.home.build()} replace />;
  }

  return (
    <Navigate
      replace
      to={appPaths.playbookAgentResult.build({
        playbookId: location.playbook_id,
        automationId: location.automation_id,
        artifactId: parsedResultId.data,
      })}
    />
  );
};
